import { ListResponse } from '@/types/common';
import fetchWithAuth from './base';
import { Contact } from '@/types/contact';
import { withSearchFilter, type FilterTree } from '@/app/components/query-builder/grouped-query';

// People CRUD operations
export const createContact = async (clientId: string, data: Partial<Contact>) => {
  const response = await fetchWithAuth(`/api/client/${clientId}/contact`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  return response.json();
};

export const getContacts = async ({
  clientId,
  startingAfter,
  endingBefore,
  companyId,
  filterSearchParams,
  limit = 100,
  search,
  filters,
  match,
  filter,
}: {
  clientId: string;
  startingAfter?: string;
  endingBefore?: string;
  companyId?: string;
  filterSearchParams?: URLSearchParams;
  limit?: number;
  search?: string;
  filters?: Array<{ field: string; operator: string; value: unknown }>;
  match?: 'all' | 'any';
  targetPages?: Array<{
    conditions: Array<{ field: string; operator: string; value: unknown }>;
  }>;
  filter?: FilterTree;
}): Promise<ListResponse<Contact>> => {
  const params = new URLSearchParams(filterSearchParams || {});
  if (startingAfter) {
    params.append('starting_after', startingAfter);
  }
  if (companyId) {
    params.append('company_id', companyId);
  }
  if (endingBefore) {
    params.append('ending_before', endingBefore);
  }
  params.set('limit', limit.toString());
  const requestFilters = [
    ...(filters || []),
    ...(search ? [{ field: 'search', operator: 'eq', value: search }] : []),
    ...(companyId ? [{ field: 'company_id', operator: 'eq', value: companyId }] : []),
    ...Array.from(new URLSearchParams(filterSearchParams || {}).entries()).map(
      ([field, value]) => ({
        field,
        operator: 'eq',
        value,
      })
    ),
  ];

  const response =
    filter || requestFilters.length
      ? await fetchWithAuth(`/api/client/${clientId}/contact/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...(startingAfter ? { starting_after: startingAfter } : {}),
            ...(endingBefore ? { ending_before: endingBefore } : {}),
            limit,
            ...(filter
              ? { filter: withSearchFilter(filter, search) }
              : {
                  ...(requestFilters.length
                    ? { filter: { match: match || 'all', rules: requestFilters } }
                    : {}),
                }),
          }),
        })
      : await fetchWithAuth(
          `/api/client/${clientId}/contact${params.size ? `?${params.toString()}` : ''}`,
          { method: 'GET' }
        );
  return response.json();
};

export type ContactCountFilter = { field: string; operator: string; value: unknown };
export type ContactCountFilterPayload = {
  match?: 'all' | 'any';
  filters?: ContactCountFilter[];
  targetPages?: Array<{ conditions: ContactCountFilter[] }>;
  filter?: FilterTree;
};

export const getContactCount = async (
  clientId: string,
  { match, filters, filter }: ContactCountFilterPayload = {}
): Promise<{
  total: number;
  persona: number;
  archived: number;
  unsubscribed: number;
  deleted: number;
}> => {
  const hasFilters = Boolean(filter || filters?.length);
  const response = await fetchWithAuth(`/api/client/${clientId}/contact/count`, {
    method: 'POST',
    ...(hasFilters
      ? {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...(filter
              ? { filter }
              : {
                  ...(filters?.length ? { filter: { match: match || 'all', rules: filters } } : {}),
                }),
          }),
        }
      : {}),
  });
  return response.json();
};

export const getContact = async (clientId: string, id: string): Promise<Contact> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/contact/${id}`, {
    method: 'GET',
  });
  return response.json();
};

export type UpdateContactPayload = Partial<Contact> & { company_id?: string };

export const updateContact = async (clientId: string, id: string, data: UpdateContactPayload) => {
  const response = await fetchWithAuth(`/api/client/${clientId}/contact/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  return response.json();
};

export const deleteContact = async (clientId: string, id: string) => {
  const response = await fetchWithAuth(`/api/client/${clientId}/contact/${id}`, {
    method: 'DELETE',
  });
  if (response.status === 204) {
    return null;
  }
  return response.json();
};

export const restoreContact = async (clientId: string, id: string) => {
  const response = await fetchWithAuth(`/api/client/${clientId}/contact/${id}/restore`, {
    method: 'POST',
  });
  if (response.status === 204) {
    return null;
  }
  return response.json();
};

export const revealContactEmail = async (clientId: string, contactId: string): Promise<Contact> => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/contact/${contactId}/reveal_email`,
    {
      method: 'POST',
    }
  );
  return response.json();
};

export const revealContactPhone = async (clientId: string, contactId: string): Promise<Contact> => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/contact/${contactId}/reveal_phone`,
    {
      method: 'POST',
    }
  );
  return response.json();
};
